import type { FeatureFlagAudience, FeatureFlagRow } from "@/api/admin.api";

export const AUDIENCES: { value: FeatureFlagAudience; label: string; hint: string }[] = [
  { value: "OFF", label: "Off", hint: "Nobody sees it." },
  { value: "TESTERS", label: "Testers", hint: "Only the emails listed below." },
  { value: "EVERYONE", label: "Everyone", hint: "Every user of the product." },
];

export const PRODUCT_LABEL: Record<FeatureFlagRow["product"], string> = {
  THERESUME: "theResume",
  THERECRUIT: "theRecruit",
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The tester list as typed: one email per line, or separated by commas, semicolons or spaces.
 * Trimmed, lowercased and deduplicated, as the server stores them.
 */
export const parseEmails = (text: string) => {
  const all = [
    ...new Set(
      text
        .split(/[\s,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
    ),
  ];
  return { emails: all.filter((e) => EMAIL.test(e)), invalid: all.filter((e) => !EMAIL.test(e)) };
};

/** A flag's state on screen, before it is saved. */
export type FlagDraft = { audience: FeatureFlagAudience; testersText: string };

export const draftOf = (flag: FeatureFlagRow): FlagDraft => ({
  audience: flag.audience,
  testersText: flag.testerEmails.join("\n"),
});

const sameList = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, i) => value === b[i]);

export const isDirty = (flag: FeatureFlagRow, draft: FlagDraft) =>
  draft.audience !== flag.audience ||
  !sameList(parseEmails(draft.testersText).emails, flag.testerEmails);

/** Why the draft can't be saved yet, or null when it can. */
export const draftProblem = (draft: FlagDraft): string | null => {
  const { emails, invalid } = parseEmails(draft.testersText);
  if (invalid.length > 0) return `Not an email: ${invalid.join(", ")}`;
  if (draft.audience === "TESTERS" && emails.length === 0) {
    return "Add at least one tester email, or turn the flag off.";
  }
  return null;
};

/** Who a flag is on for, in a few words: "Off", "On for 2 testers", "On for everyone". */
export const describeAudience = (audience: FeatureFlagAudience, testers: number) =>
  audience === "OFF"
    ? "Off"
    : audience === "EVERYONE"
    ? "On for everyone"
    : `On for ${testers} ${testers === 1 ? "tester" : "testers"}`;
